import React from 'react';

type PreviewCell = {
  label: React.ReactNode;
  className?: string;
  colSpan?: number;
  rowSpan?: number;
};

type PreviewColumn = {
  label: React.ReactNode;
  className?: string;
  colClassName?: string;
};

type DisabledDataFramePreviewProps = {
  title?: string;
  columns: readonly (string | PreviewColumn)[];
  headerRows?: readonly (readonly (string | PreviewCell)[])[];
  rows?: number;
  className?: string;
  scrollClassName?: string;
  tableClassName?: string;
};

const annualBaseColumns = [
  ['순', 'cp-annual-progress-col-seq'],
  ['날짜', 'cp-annual-progress-col-date'],
  ['요일', 'cp-annual-progress-col-weekday'],
  ['교시', 'cp-annual-progress-col-period'],
  ['학년', 'cp-annual-progress-col-grade'],
  ['학기', 'cp-annual-progress-col-semester'],
  ['편제', 'cp-annual-progress-col-template'],
  ['구분', 'cp-annual-progress-col-program'],
  ['시간표', 'cp-annual-progress-col-timetable'],
  ['단원', 'cp-annual-progress-col-unit'],
  ['학습내용', 'cp-annual-progress-col-learning'],
  ['쪽수', 'cp-annual-progress-col-metric'],
  ['보조쪽수', 'cp-annual-progress-col-metric'],
  ['해당 차시', 'cp-annual-progress-col-metric-period'],
  ['전체 차시', 'cp-annual-progress-col-metric-period'],
  ['준비물', 'cp-annual-progress-col-metric'],
  ['범교과', 'cp-annual-progress-col-record cp-annual-progress-col-record-fill'],
  ['메모', 'cp-annual-progress-col-record cp-annual-progress-col-record-fill'],
  ['평가', 'cp-annual-progress-col-record cp-annual-progress-col-record-fill'],
  ['수업기록', 'cp-annual-progress-col-suhub-girog cp-annual-progress-col-record-fill'],
] as const;

export const PROGRAM_PROGRESS_PREVIEW_COLUMNS: readonly PreviewColumn[] = annualBaseColumns.map(([label, className]) => ({
  label,
  className,
  colClassName: className,
}));

export const ANNUAL_PROGRESS_PREVIEW_COLUMNS: readonly PreviewColumn[] = [
  ...PROGRAM_PROGRESS_PREVIEW_COLUMNS,
  {
    label: '행동특성',
    className: 'cp-annual-progress-col-log cp-annual-progress-behavior-head-th',
    colClassName: 'cp-annual-progress-col-log',
  },
];

export const PROGRAM_PROGRESS_PREVIEW_HEADER_ROWS: readonly (readonly PreviewCell[])[] = [
  [
    { label: '프로그램 일정', colSpan: 9, className: 'cp-annual-progress-head-group cp-annual-progress-head-group-base' },
    { label: '프로그램 내용(편집)', colSpan: 7, className: 'cp-annual-progress-head-group cp-annual-progress-head-group-learning' },
    { label: '메모 및 기록(편집)', colSpan: 4, className: 'cp-annual-progress-head-group cp-annual-progress-head-group-record' },
  ],
  PROGRAM_PROGRESS_PREVIEW_COLUMNS.map(({ label, className }) => ({ label, className })),
];

export const ANNUAL_PROGRESS_PREVIEW_HEADER_ROWS: readonly (readonly PreviewCell[])[] = [
  [
    { label: '프로그램 일정', colSpan: 9, className: 'cp-annual-progress-head-group cp-annual-progress-head-group-base' },
    { label: '프로그램 내용', colSpan: 7, className: 'cp-annual-progress-head-group cp-annual-progress-head-group-learning' },
    { label: '메모 및 기록', colSpan: 4, className: 'cp-annual-progress-head-group cp-annual-progress-head-group-record' },
    { label: '누가기록', className: 'cp-annual-progress-head-group cp-annual-progress-head-group-log' },
  ],
  ANNUAL_PROGRESS_PREVIEW_COLUMNS.map(({ label, className }) => ({ label, className })),
];

export const CLASS_RECORD_MEMOS_PREVIEW_COLUMNS: readonly PreviewColumn[] = [
  ['학기', 'cp-crm-col cp-crm-col--term'],
  ['날짜', 'cp-crm-col cp-crm-col--date'],
  ['요일', 'cp-crm-col cp-crm-col--weekday'],
  ['교시', 'cp-crm-col cp-crm-col--period'],
  ['프로그램', 'cp-crm-col cp-crm-col--program'],
  ['단원', 'cp-crm-col cp-crm-col--unit'],
  ['학습내용', 'cp-crm-col cp-crm-col--learning'],
  ['구분', 'cp-crm-col cp-crm-col--kind'],
  ['메모', 'cp-crm-col cp-crm-col--note'],
].map(([label, className]) => ({ label, className, colClassName: className }));

export const WEEKLY_GUIDE_PREVIEW_COLUMNS: readonly PreviewColumn[] = [
  { label: '구분', className: 'cp-weekly-learning-guide-title-col' },
  { label: '월', className: 'cp-weekly-learning-guide-day-head' },
  { label: '화', className: 'cp-weekly-learning-guide-day-head' },
  { label: '수', className: 'cp-weekly-learning-guide-day-head' },
  { label: '목', className: 'cp-weekly-learning-guide-day-head' },
  { label: '금', className: 'cp-weekly-learning-guide-day-head' },
];

export const WEEKLY_GUIDE_PREVIEW_HEADER_ROWS: readonly (readonly PreviewCell[])[] = [
  [
    { label: '구분', rowSpan: 2, className: 'cp-weekly-learning-guide-title-col' },
    { label: '월', className: 'cp-weekly-learning-guide-day-head' },
    { label: '화', className: 'cp-weekly-learning-guide-day-head' },
    { label: '수', className: 'cp-weekly-learning-guide-day-head' },
    { label: '목', className: 'cp-weekly-learning-guide-day-head' },
    { label: '금', className: 'cp-weekly-learning-guide-day-head' },
  ],
  [
    { label: '3/2', className: 'cp-weekly-learning-guide-date-head' },
    { label: '3/3', className: 'cp-weekly-learning-guide-date-head' },
    { label: '3/4', className: 'cp-weekly-learning-guide-date-head' },
    { label: '3/5', className: 'cp-weekly-learning-guide-date-head' },
    { label: '3/6', className: 'cp-weekly-learning-guide-date-head' },
  ],
];

export const ANNUAL_TIMETABLE_PREVIEW_COLUMNS: readonly PreviewColumn[] = [
  { label: '월', className: 'cp-weekly-schedule-th-month' },
  { label: '주', className: 'cp-weekly-schedule-th-week' },
  { label: '일자', className: 'cp-weekly-schedule-th-daterange' },
  { label: '월', className: 'cp-weekly-schedule-th-weekday' },
  { label: '화', className: 'cp-weekly-schedule-th-weekday' },
  { label: '수', className: 'cp-weekly-schedule-th-weekday' },
  { label: '목', className: 'cp-weekly-schedule-th-weekday' },
  { label: '금', className: 'cp-weekly-schedule-th-weekday' },
  { label: '수업시수', className: 'cp-weekly-schedule-th-hours' },
];

const DisabledDataFramePreview: React.FC<DisabledDataFramePreviewProps> = ({
  title,
  columns,
  headerRows,
  rows = 8,
  className = '',
  scrollClassName = '',
  tableClassName = '',
}) => {
  const rowIndexes = Array.from({ length: Math.max(1, rows) }, (_, idx) => idx);
  const normalizedColumns = columns.map((column) =>
    typeof column === 'string' ? { label: column } : column
  );
  const normalizedHeaderRows: PreviewCell[][] =
    headerRows?.map((row) =>
      row.map((cell) => (typeof cell === 'string' ? { label: cell } : cell))
    ) ?? [normalizedColumns.map(({ label, className }) => ({ label, className }))];

  return (
    <div className={['cp-disabled-data-frame-preview', className].filter(Boolean).join(' ')} aria-hidden="true">
      {title ? <div className="cp-disabled-data-frame-preview__title">{title}</div> : null}
      <div className={['cp-disabled-data-frame-preview__scroll', scrollClassName].filter(Boolean).join(' ')}>
        <table className={['cp-disabled-data-frame-preview__table', tableClassName].filter(Boolean).join(' ')}>
          {normalizedColumns.some((column) => column.colClassName) ? (
            <colgroup>
              {normalizedColumns.map((column, index) => (
                <col key={index} className={column.colClassName} />
              ))}
            </colgroup>
          ) : null}
          <thead>
            {normalizedHeaderRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <th
                    key={`${rowIndex}-${cellIndex}`}
                    className={cell.className}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan}
                  >
                    {cell.label}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rowIndexes.map((rowIndex) => (
              <tr key={rowIndex}>
                {normalizedColumns.map((column, columnIndex) => (
                  <td key={`${rowIndex}-${columnIndex}`} className={column.className}>
                    <span className="cp-disabled-data-frame-preview__placeholder" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DisabledDataFramePreview;
